import { useOverlayState } from "@heroui/react";
import { isApiError } from "@upsquad/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequestOtpMutation, useVerifyOtpMutation } from "@/src/auth/services/auth-http.service";
import { Button, InputOTP, Modal } from "@/src/common/components/ui";

interface OtpVerifyModalProps {
	email: string;
	isOpen: boolean;
	onClose: () => void;
	onAuthSuccess: (result: unknown) => void;
}

const RESEND_COOLDOWN = 90;
const OTP_LENGTH = 6;

export function OtpVerifyModal({ email, isOpen, onClose, onAuthSuccess }: OtpVerifyModalProps) {
	const { t } = useTranslation("auth");
	const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
	const [requestOtp, { isLoading: isResending }] = useRequestOtpMutation();
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);

	const state = useOverlayState({
		isOpen,
		onOpenChange: (open) => {
			if (!open) onClose();
		},
	});

	useEffect(() => {
		if (resendTimer <= 0) return;
		const interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
		return () => clearInterval(interval);
	}, [resendTimer]);

	const handleComplete = async (value: string) => {
		setError(null);
		try {
			const result = await verifyOtp({ email, code: value }).unwrap();
			if (result && "user" in result && "access_token" in result && "communities" in result) {
				onClose();
				onAuthSuccess(result);
			}
		} catch (err: unknown) {
			const errorKey = isApiError(err) ? err.data.error : undefined;
			if (errorKey === "invalid_code") setError(t("invalid_code"));
			else if (errorKey === "expired") setError(t("code_expired"));
			else if (errorKey === "max_attempts") setError(t("max_attempts"));
			else setError(t("invalid_code"));
		}
	};

	const handleResend = async () => {
		setError(null);
		try {
			await requestOtp({ email }).unwrap();
			setResendTimer(RESEND_COOLDOWN);
			setCode("");
		} catch {
			// silently fail
		}
	};

	return (
		<Modal state={state}>
			<Modal.Backdrop isDismissable={false}>
				<Modal.Container>
					<Modal.Dialog>
						<Modal.Header>
							<Modal.Heading className="text-xl font-semibold">{t("otp_title")}</Modal.Heading>
						</Modal.Header>
						<Modal.Body className="flex flex-col items-center gap-4">
							<p className="text-foreground/50 text-center">{t("otp_subtitle", { email })}</p>

							<InputOTP maxLength={OTP_LENGTH} value={code} onChange={setCode} onComplete={handleComplete} isDisabled={isVerifying}>
								<InputOTP.Group>
									{Array.from({ length: OTP_LENGTH }, (_, i) => (
										<InputOTP.Slot key={i} index={i} />
									))}
								</InputOTP.Group>
							</InputOTP>

							{error && <p className="text-danger text-sm">{error}</p>}

							<p className="text-sm text-default-500 mt-3">{t("otp_spam_warning")}</p>

							<Button variant="ghost" size="sm" onPress={handleResend} isDisabled={resendTimer > 0 || isResending}>
								{resendTimer > 0 ? t("resend_in", { seconds: resendTimer }) : t("resend_code")}
							</Button>
						</Modal.Body>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
